import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import { sentryService } from "../sentry";
import type {
  BindOnDone,
  ConfigOf,
  FlowState,
  Needs,
  Outcomes,
  ScreenKey,
  Tagged,
  TaggedUnion,
  TransitionTable,
} from "./";
import { flowBrand, subflowBrand } from "./internals";

// Types are erased below this line: the reducer, the flattener and the
// projection walk plain objects. Correctness is enforced upstream by
// `TransitionTable<TConfig, TCtx, TDone>` and `FlowResult<TConfig, K>`.

type RuntimeSubflowState = {
  data: Record<string, unknown>;
  subflows?: RuntimeSubflows;
  run: number;
};
type RuntimeSubflows = Record<string, RuntimeSubflowState>;
type RuntimeState = {
  tag: string;
  data: Record<string, unknown>;
  subflows?: RuntimeSubflows;
};

/** One visit as the reducer holds it: the flattened tag and the plain bag. */
export type RawFlowFrame = Readonly<RuntimeState>;
type RuntimeOutcome = Tagged<string, unknown>;
type RuntimeOnDone = (outcome: RuntimeOutcome) => void;

type ForwardRuntimeAction = {
  tag: string;
  data?: Record<string, unknown>;
  subflows?: RuntimeSubflows;
};
type BackRuntimeAction = { type: "back" };
// `runs` scopes the search to every mount run enclosing the rewind. Comparing
// the full path matters for nested mounts: inner run 1 can exist in outer runs
// 1 and 2, but those are not the same child session.
type RewindRuntimeAction = { type: "rewind"; tag: string; runs?: number[] };
type DoneRuntimeAction = { type: "done"; result: RuntimeOutcome };

type HandlerAction =
  | ForwardRuntimeAction
  | BackRuntimeAction
  | RewindRuntimeAction
  | DoneRuntimeAction;

// Handler results minus `done` — the reducer never sees a done action.
type DispatchAction =
  | ForwardRuntimeAction
  | BackRuntimeAction
  | { type: "forward" }
  | { type: "goToIndex"; index: number }
  | RewindRuntimeAction;

type RuntimeAction = DispatchAction | DoneRuntimeAction;

function isBackAction(action: RuntimeAction): action is BackRuntimeAction {
  return "type" in action && action.type === "back";
}

function isForwardAction(action: RuntimeAction): action is { type: "forward" } {
  return "type" in action && action.type === "forward";
}

function isGoToIndexAction(
  action: RuntimeAction,
): action is { type: "goToIndex"; index: number } {
  return "type" in action && action.type === "goToIndex";
}

function isRewindAction(action: RuntimeAction): action is RewindRuntimeAction {
  return "type" in action && action.type === "rewind";
}

function isDoneAction(action: RuntimeAction): action is DoneRuntimeAction {
  return "type" in action && action.type === "done";
}

function subflowState(
  state: Pick<RuntimeState, "subflows">,
  key: string,
): RuntimeSubflowState | undefined {
  return state.subflows?.[key];
}

// The rewind tag is the mount path of its target (`K.B.S`). Its identity is
// every enclosing run, not just the innermost one.
function runPathAt(state: RuntimeState, tag: string): number[] | undefined {
  const runs: number[] = [];
  let current: Pick<RuntimeState, "subflows"> = state;
  for (const key of tag.split(".").slice(0, -1)) {
    const child = subflowState(current, key);
    if (!child) {
      return undefined;
    }
    runs.push(child.run);
    current = child;
  }
  return runs;
}

function sameRunPath(
  left: readonly number[] | undefined,
  right: readonly number[],
): boolean {
  return (
    left?.length === right.length &&
    left.every((run, index) => run === right[index])
  );
}

function buildState(
  tag: string,
  current: RuntimeState,
  actionData: Record<string, unknown>,
  actionSubflows?: RuntimeSubflows,
): RuntimeState {
  const data = current.data ?? {};
  const subflows = actionSubflows
    ? { ...current.subflows, ...actionSubflows }
    : current.subflows;
  return {
    tag,
    data: {
      ...data,
      ...actionData,
    },
    ...(subflows ? { subflows } : {}),
  };
}

// Terminal screens (`outcomes: never`) are excluded because `TaggedUnion<never>`
// would otherwise leak `Tagged<string, never>` into the outcome union
// (`keyof never & string` resolves to `string`), breaking narrowing on `from.tag`.
type FlowOutcomeEvent<TConfig> = {
  [K in ScreenKey<TConfig>]: TConfig[K] extends { flow: infer F }
    ? MountOutcomeEvent<TConfig, K, ConfigOf<F>>
    : [Outcomes<TConfig, K>] extends [never]
      ? never
      : {
          from: Tagged<K, Needs<TConfig, K>>;
          outcome: TaggedUnion<Outcomes<TConfig, K>>;
        };
}[ScreenKey<TConfig>];

// A child screen's transition arrives on the host's callback as the projected
// host frame, so `from.sub.state` is the child screen and `outcome` is its own.
type MountOutcomeEvent<TConfig, K extends ScreenKey<TConfig>, TChild> =
  FlowOutcomeEvent<TChild> extends infer E
    ? E extends { from: infer From; outcome: infer O }
      ? {
          from: Tagged<K, Needs<TConfig, K>> & {
            sub: { state: From; bindOnDone: BindOnDone<TChild> };
          };
          outcome: O;
        }
      : never
    : never;

export type FlowTransition<TConfig> = FlowOutcomeEvent<TConfig> & {
  to: FlowState<TConfig>;
};

// Index is not identity: branching after a back puts a new visit at a
// discarded one's index, often under the same tag.
type Visit = { key: string; state: RuntimeState };

/** A visit with the binder that commits against that visit, not the active one. */
export type FlowVisit<TConfig> = {
  key: string;
  state: FlowState<TConfig>;
  bindOnDone: BindOnDone<TConfig>;
};

/**
 * Reads the key of the visit at `index`. `visitKeys` is index-aligned with
 * `timeline`, so a renderer walking the timeline is always in range; the throw
 * catches a caller that indexed something else.
 */
export function visitKeyAt(
  visitKeys: readonly string[],
  index: number,
): string {
  const key = visitKeys[index];
  if (key === undefined) {
    throw new Error(`useNavigableFlow: no visit at timeline index ${index}`);
  }
  return key;
}

type InternalState = {
  timeline: Visit[];
  activeIndex: number;
  visitCount: number;
};

function getCurrent(state: InternalState): Visit {
  const current = state.timeline[state.activeIndex];
  if (!current) {
    throw new Error("useNavigableFlow: active timeline index is out of bounds");
  }
  return current;
}

type DispatchedAction = {
  /** The visit whose outcome produced this, or `null` for a control. */
  from: string | null;
  action: DispatchAction;
};

function apply(state: InternalState, action: DispatchAction): InternalState {
  if (isBackAction(action)) {
    return state.activeIndex > 0
      ? { ...state, activeIndex: state.activeIndex - 1 }
      : state;
  }
  if (isForwardAction(action)) {
    return state.activeIndex < state.timeline.length - 1
      ? { ...state, activeIndex: state.activeIndex + 1 }
      : state;
  }
  if (isGoToIndexAction(action)) {
    return state.timeline[action.index]
      ? { ...state, activeIndex: action.index }
      : state;
  }
  if (isRewindAction(action)) {
    for (let index = state.activeIndex - 1; index >= 0; index -= 1) {
      const frame = state.timeline[index]?.state;
      if (
        frame?.tag === action.tag &&
        (action.runs === undefined ||
          sameRunPath(runPathAt(frame, action.tag), action.runs))
      ) {
        return { ...state, activeIndex: index };
      }
    }
    return state;
  }

  const previous = getCurrent(state);
  const current = buildState(
    action.tag,
    previous.state,
    action.data ?? {},
    action.subflows,
  );

  const activeTimeline = state.timeline.slice(0, state.activeIndex + 1);
  return {
    // A new navigation after going back creates a new branch, matching browser
    // history by discarding the no-longer-reachable forward visits.
    timeline: [
      ...activeTimeline,
      { key: `visit-${state.visitCount}`, state: current },
    ],
    activeIndex: activeTimeline.length,
    visitCount: state.visitCount + 1,
  };
}

function reducer(
  state: InternalState,
  { from, action }: DispatchedAction,
): InternalState {
  if (from !== null && getCurrent(state).key !== from) {
    return state;
  }
  return apply(state, action);
}

// Screen `S` of child `K` becomes host screen `K.S`. Child state is stored in
// the frame's dedicated `subflows[K]` namespace, never in the host data bag.

type RuntimeHandler = (
  output: unknown,
  ctx: unknown,
  current: RuntimeState,
) => HandlerAction;

type RuntimeHandlers = Record<string, RuntimeHandler>;

type RuntimeSubflowEntry = {
  [subflowBrand]: true;
  flow: {
    initial: (start: Record<string, unknown>) => RuntimeState;
    transitions: RuntimeTable;
  };
  onDone: RuntimeHandlers;
};

type RuntimeTable = Record<string, RuntimeHandlers | RuntimeSubflowEntry>;

// The table's handler types have no structural relation to the runtime view;
// this mapped type is the only guarantee.
function asRuntimeTable(transitions: unknown): RuntimeTable {
  return transitions as RuntimeTable;
}

// Exhaustiveness is a compile-time guarantee of `TransitionTable`, so a miss
// here means the table was reached past the public API.
function requireHandler(
  handler: RuntimeHandler | undefined,
  key: string,
): RuntimeHandler {
  if (!handler) {
    throw new Error(`useNavigableFlow: no transition handler for ${key}`);
  }
  return handler;
}

function isSubflowEntry(
  entry: RuntimeHandlers | RuntimeSubflowEntry,
): entry is RuntimeSubflowEntry {
  // The brand, not the shape: a plain screen may declare outcomes named `flow`
  // and `onDone`, and its handler map would otherwise read as a mount.
  return subflowBrand in entry;
}

function mapHandlers(
  handlers: RuntimeHandlers,
  wrap: (handler: RuntimeHandler) => RuntimeHandler,
): RuntimeHandlers {
  return Object.fromEntries(
    Object.entries(handlers).map(([outcome, handler]) => [
      outcome,
      wrap(handler),
    ]),
  );
}

function flattenTransitions(
  transitions: RuntimeTable,
): Record<string, RuntimeHandlers> {
  const subs: Record<string, RuntimeSubflowEntry> = {};
  for (const [key, entry] of Object.entries(transitions)) {
    // `ScreenKey` already excludes dots; this catches callers that bypass it.
    if (key.includes(".")) {
      throw new Error(`useNavigableFlow: screen key "${key}" contains a dot`);
    }
    if (isSubflowEntry(entry)) {
      subs[key] = entry;
    }
  }

  // A mount requires its complete Start object instead of inheriting fields
  // from the host bag. That exact object seeds the child and remains ordinary
  // host data, while the child's evolving state lives in `subflows`.
  const enterSubflow = (
    action: HandlerAction,
    current: RuntimeState,
  ): HandlerAction => {
    if (
      isBackAction(action) ||
      isRewindAction(action) ||
      isDoneAction(action)
    ) {
      return action;
    }
    const sub = subs[action.tag];
    if (!sub) {
      return action;
    }
    const start = action.data ?? {};
    const childInitial = sub.flow.initial(start);
    const previousRun = subflowState(current, action.tag)?.run ?? 0;
    return {
      tag: `${action.tag}.${childInitial.tag}`,
      data: start,
      subflows: {
        [action.tag]: {
          data: childInitial.data,
          ...(childInitial.subflows ? { subflows: childInitial.subflows } : {}),
          run: previousRun + 1,
        },
      },
    };
  };

  const flat: Record<string, RuntimeHandlers> = {};
  for (const [key, entry] of Object.entries(transitions)) {
    if (!isSubflowEntry(entry)) {
      flat[key] = mapHandlers(
        entry,
        handler => (output, ctx, current) =>
          enterSubflow(handler(output, ctx, current), current),
      );
      continue;
    }
    // Recursing first is what makes nesting work: the child's own sub-flows are
    // already keyed `B.S`, so lifting them here produces `K.B.S`.
    const childFlat = flattenTransitions(entry.flow.transitions);
    for (const [childTag, handlers] of Object.entries(childFlat)) {
      flat[`${key}.${childTag}`] = mapHandlers(
        handlers,
        handler => (output, ctx, current) => {
          const child = subflowState(current, key);
          if (!child) {
            throw new Error(
              `useNavigableFlow: no subflow state for mount ${key}`,
            );
          }
          const result = handler(output, ctx, {
            tag: childTag,
            data: child.data,
            ...(child.subflows ? { subflows: child.subflows } : {}),
          });
          if (isDoneAction(result)) {
            return enterSubflow(
              requireHandler(
                entry.onDone[result.result.tag],
                `${key}.${result.result.tag}`,
              )(result.result.data, ctx, { tag: key, data: current.data }),
              current,
            );
          }
          if (isRewindAction(result)) {
            return {
              type: "rewind",
              tag: `${key}.${result.tag}`,
              runs: [child.run, ...(result.runs ?? [])],
            };
          }
          if (isBackAction(result)) {
            // Back on the child's first screen is the ordinary pop that lands
            // on the host screen which entered it.
            return result;
          }
          // The engine's merge is shallow, so the whole child bag is
          // re-emitted.
          return {
            tag: `${key}.${result.tag}`,
            subflows: {
              [key]: {
                data: { ...child.data, ...(result.data ?? {}) },
                ...(result.subflows || child.subflows
                  ? {
                      subflows: {
                        ...child.subflows,
                        ...result.subflows,
                      },
                    }
                  : {}),
                run: child.run,
              },
            },
          };
        },
      );
    }
  }
  return flat;
}

type ProjectedState = RuntimeState & {
  sub?: { state: ProjectedState; bindOnDone: (tag: string) => RuntimeOnDone };
};

function projectState(
  state: RuntimeState,
  bind: (tag: string) => RuntimeOnDone,
): ProjectedState {
  const dot = state.tag.indexOf(".");
  if (dot < 0) {
    // Keep engine state out of the public frame. Plain flows preserve object
    // identity; a host that has mounted a child gets one cached projection.
    return state.subflows ? { tag: state.tag, data: state.data } : state;
  }
  const key = state.tag.slice(0, dot);
  const child = subflowState(state, key);
  if (!child) {
    throw new Error(`useNavigableFlow: no subflow state for mount ${key}`);
  }
  const bindChild = (tag: string) => bind(`${key}.${tag}`);
  return {
    tag: key,
    data: state.data,
    sub: {
      state: projectState(
        {
          tag: state.tag.slice(dot + 1),
          data: child.data,
          ...(child.subflows ? { subflows: child.subflows } : {}),
        },
        bindChild,
      ),
      bindOnDone: bindChild,
    },
  };
}

type RuntimeInitial = RuntimeState | ((start: unknown) => RuntimeState);

type FlowInitial<TConfig, TStart> = undefined extends TStart
  ? {
      initial: FlowState<TConfig> | ((start: TStart) => FlowState<TConfig>);
      start?: TStart;
    }
  :
      | { initial: FlowState<TConfig>; start?: TStart }
      | { initial: (start: TStart) => FlowState<TConfig>; start: TStart };

function initialiseState(config: {
  initial: unknown;
  start?: unknown;
}): InternalState {
  // `RuntimeState` is concrete, so the `typeof` check narrows here where it
  // cannot against the unresolved `FlowState<TConfig>` union.
  const initial = config.initial as RuntimeInitial;
  return {
    timeline: [
      {
        key: "visit-0",
        state: typeof initial === "function" ? initial(config.start) : initial,
      },
    ],
    activeIndex: 0,
    visitCount: 1,
  };
}

export function useNavigableFlow<
  TConfig,
  TCtx = void,
  TDone = never,
  TStart = void,
>(
  config: {
    transitions: TransitionTable<TConfig, TCtx, TDone>;
    ctx: TCtx;
    /**
     * Fires when a handler returns `done(result)`.
     */
    onDone?: (result: TaggedUnion<TDone>) => void;
    onTransition?: (event: FlowTransition<TConfig>) => void;
    /**
     * Fires with the committed state on first render and after every
     * transition — always a render cycle after `onTransition`, which runs
     * synchronously in the outcome handler. Observability only; it cannot
     * redirect.
     */
    onStateLoaded?: (state: FlowState<TConfig>) => void;
    // Spread from a `defineFlow` flow: the only inference site for these four.
    readonly [flowBrand]?: {
      config: TConfig[];
      start: TStart[];
      done: TDone[];
      ctx: TCtx[];
    };
  } & FlowInitial<TConfig, TStart>,
) {
  const { ctx, onDone, onTransition, onStateLoaded } = config;

  const runtimeTransitions = asRuntimeTable(config.transitions);
  const transitions = useMemo(
    () => flattenTransitions(runtimeTransitions),
    [runtimeTransitions],
  );

  const [state, dispatch] = useReducer(reducer, config, initialiseState);
  // Two outcomes can dispatch in one batch, so the staleness check has to see
  // the first before React commits it. `dispatchQueued` is the only writer, so
  // the ref only ever moves forward; re-seeding it from a render would rewind
  // it behind a queued action.
  const queuedRef = useRef(state);

  const dispatchQueued = useCallback((dispatched: DispatchedAction) => {
    const next = reducer(queuedRef.current, dispatched);
    queuedRef.current = next;
    dispatch(dispatched);
    return next;
  }, []);

  // Latest-ref so a visit's binder stays identity-stable — projected frames are
  // memoised per visit.
  const handleOutcomeRef = useRef<
    (visit: Visit, tag: string, outcome: RuntimeOutcome) => void
  >(() => undefined);
  const bindersRef = useRef(
    new WeakMap<Visit, (tag: string) => RuntimeOnDone>(),
  );
  const bindVisit = useCallback((visit: Visit) => {
    const cached = bindersRef.current.get(visit);
    if (cached) {
      return cached;
    }
    const bind =
      (tag: string): RuntimeOnDone =>
      outcome =>
        handleOutcomeRef.current(visit, tag, outcome);
    bindersRef.current.set(visit, bind);
    return bind;
  }, []);
  // Memoised per visit: consumers key effects on `state` identity.
  const projectionsRef = useRef(new WeakMap<Visit, ProjectedState>());
  const project = useCallback(
    (visit: Visit) => {
      const cached = projectionsRef.current.get(visit);
      if (cached) {
        return cached;
      }
      const projected = projectState(visit.state, bindVisit(visit));
      projectionsRef.current.set(visit, projected);
      return projected;
    },
    [bindVisit],
  );

  // Latest-ref so an inline callback prop neither re-fires an unchanged state
  // nor goes stale — the effect below keys on the committed state alone.
  const onStateLoadedRef = useRef(onStateLoaded);
  onStateLoadedRef.current = onStateLoaded;
  const currentVisit = getCurrent(state);
  const current = project(currentVisit) as FlowState<TConfig>;
  useEffect(() => {
    onStateLoadedRef.current?.(current);
  }, [current]);

  const currentTag = currentVisit.state.tag;

  // A `done()` leaves the cursor on the visit that exited, so nothing else
  // retires it: without this, a second submission in the same batch runs the
  // handler and fires `onTransition`/`onDone` again.
  const exitedRef = useRef(new Set<string>());

  const handleOutcome = useCallback(
    (visit: Visit, tag: string, outcome: RuntimeOutcome) => {
      const queued = queuedRef.current;
      if (exitedRef.current.has(visit.key)) {
        return;
      }
      if (getCurrent(queued).key !== visit.key) {
        // Late outcome from a screen the member left: the handler would build
        // the target from whichever visit is active now.
        return;
      }
      if (visit.state.tag !== tag) {
        // `BindOnDone` accepts any plain key, so only the runtime can see a
        // frame bound under another screen's tag.
        throw new Error(
          `useNavigableFlow: bindOnDone("${tag}") on a "${visit.state.tag}" visit`,
        );
      }
      const action = requireHandler(
        transitions[tag]?.[outcome.tag],
        `${tag}.${outcome.tag}`,
      )(outcome.data, ctx, visit.state);
      const from = project(visit);
      if (isDoneAction(action)) {
        exitedRef.current.add(visit.key);
        // The exit is still an outcome event; only the dispatch is skipped, so
        // the timeline and cursor stay where they are.
        onTransition?.({
          from,
          outcome,
          to: from,
        } as FlowTransition<TConfig>);
        onDone?.(action.result as TaggedUnion<TDone>);
        return;
      }
      const next = dispatchQueued({ from: visit.key, action });
      // Every outcome that got this far is the active visit's, so it is never
      // stale. One the timeline cannot move on — `back()` at index 0, a rewind
      // to a screen the member never visited — is still the member's outcome,
      // and reports `to === from` because the visit did not change.
      // Correlated union: TS can't see that `source.tag === tag` is invariant
      // when handleOutcome is called, so it can't prove the object lands in the
      // matching variant of `FlowTransition<TConfig>`.
      onTransition?.({
        from,
        outcome,
        to: project(getCurrent(next)),
      } as FlowTransition<TConfig>);
    },
    [transitions, ctx, dispatchQueued, onDone, onTransition, project],
  );
  handleOutcomeRef.current = handleOutcome;

  const hasBackHistory = state.activeIndex > 0;
  const stateHasBackOutcome = "back" in (transitions[currentTag] ?? {});

  const canGoBack = hasBackHistory && stateHasBackOutcome;

  // A control rather than a frame callback: it owns whichever visit the flow
  // stands on when it is called, so two hops in one batch walk back two visits.
  // Returns the index the flow stands on afterwards — unchanged when the screen
  // refuses Back, and more than one hop back when its handler rewinds — so a
  // caller walking several hops can see what each one did.
  const goBack = useCallback(() => {
    const visit = getCurrent(queuedRef.current);
    const tag = visit.state.tag;
    const fromHistory = queuedRef.current.activeIndex > 0;
    const hasBackOutcome = "back" in (transitions[tag] ?? {});
    if (!fromHistory || !hasBackOutcome) {
      // Warn instead of silently no-op'ing so a miswired caller is visible in dev.
      sentryService.captureMessage(
        "useNavigableFlow: goBack() called on screen but canGoBack is false",
        {
          level: "warning",
          extra: {
            currentTag: tag,
            hasBackHistory: fromHistory,
            stateHasBackOutcome: hasBackOutcome,
          },
        },
      );
      return queuedRef.current.activeIndex;
    }
    handleOutcome(
      visit,
      tag,
      // Route through the user's `back` handler rather than bypassing it.
      // The guard above already proves the handler exists.
      { tag: "back", data: {} },
    );
    return queuedRef.current.activeIndex;
  }, [handleOutcome, transitions]);

  const canGoForward = state.activeIndex < state.timeline.length - 1;
  const goForward = useCallback(() => {
    if (!canGoForward) {
      sentryService.captureMessage(
        "useNavigableFlow: goForward() called but canGoForward is false",
        { level: "warning", extra: { currentTag } },
      );
      return;
    }
    dispatchQueued({ from: null, action: { type: "forward" } });
  }, [canGoForward, currentTag, dispatchQueued]);

  const goToIndex = useCallback(
    (index: number) => {
      const queued = queuedRef.current;
      if (!queued.timeline[index]) {
        sentryService.captureMessage(
          "useNavigableFlow: goToIndex() target does not exist",
          {
            level: "warning",
            extra: {
              currentTag: getCurrent(queued).state.tag,
              index,
              timelineLength: queued.timeline.length,
            },
          },
        );
        return;
      }
      dispatchQueued({ from: null, action: { type: "goToIndex", index } });
    },
    [dispatchQueued],
  );

  const bindOnDone = bindVisit(currentVisit) as BindOnDone<TConfig>;

  const visits = useMemo(
    (): FlowVisit<TConfig>[] =>
      state.timeline.map(visit => ({
        key: visit.key,
        state: project(visit) as FlowState<TConfig>,
        bindOnDone: bindVisit(visit) as BindOnDone<TConfig>,
      })),
    [bindVisit, project, state.timeline],
  );
  const timeline = useMemo(
    () => state.timeline.map(visit => project(visit) as FlowState<TConfig>),
    [project, state.timeline],
  );
  const visitKeys = useMemo(
    () => state.timeline.map(visit => visit.key),
    [state.timeline],
  );
  const rawTimeline = useMemo(
    () => state.timeline.map(visit => visit.state),
    [state.timeline],
  );

  return {
    state: current,
    // Compatibility view: past visits through current. Persistent renderers
    // use the full timeline below so browser-forward visits remain mounted.
    stack: timeline.slice(0, state.activeIndex + 1),
    timeline,
    /**
     * One key per visit, index-aligned with `timeline`. Key mounted screens and
     * browser history entries on these, never on the timeline index.
     */
    visitKeys,
    /**
     * Every visit with its own binder. A hidden frame handed the top-level
     * `bindOnDone` commits against whichever visit is active now.
     */
    visits,
    activeIndex: state.activeIndex,
    /**
     * The flattened runtime tag of the active frame (`Intake.Second` inside a
     * mounted child). It groups animation, not identity: key frames and
     * history entries on `visitKeys`.
     */
    stateKey: currentTag,
    /**
     * The flattened runtime frames, one per visit, as the reducer holds them.
     * Plain data throughout, so this is what persistence serialises; the
     * projected `timeline` above carries child binders.
     */
    rawTimeline: rawTimeline satisfies readonly RawFlowFrame[],
    bindOnDone,
    canGoBack,
    goBack,
    canGoForward,
    goForward,
    goToIndex,
  };
}
